import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, ClockIcon, InfoIcon, MinusIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Panel } from '../../../components/ui/Panel';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ItemCard } from '../../../components/market/ItemCard';
import { FeatureGate } from '../../../components/platform/FeatureStatusBadge';
import { useAccount } from '../../../contexts/AccountContext';
import { marketCategories, marketItems, sellerFor } from '../../../data/market';
import { timetable } from '../../../data/academics';
import { naira } from '../../../utils/format';
import type { Condition, MarketItem } from '../../../types/market';

/**
 * One route, several genuinely different interfaces.
 *
 * The category's configured pattern decides which one renders. Ordering food
 * needs a cart and a delivery window; buying a used textbook needs condition and
 * course linkage; a room needs distance and an inspection state. Collapsing them
 * into one filtered list is what made the old marketplace read as an index.
 */
export function MarketCategory() {
  const {
    categoryId
  } = useParams<{
    categoryId: string;
  }>();
  const category = marketCategories.find(entry => entry.id === categoryId);
  const {
    campusName
  } = useAccount();
  if (!category) {
    return <EmptyState title="That category does not exist" description="Marketplace categories are configuration. If this link came from somewhere in Campuzon, the category may have been retired." action={<Link to="/market" className="inline-flex min-h-touch items-center text-body font-semibold text-brand-700 hover:text-brand-800" data-id="element-10209" data-mp="3dfkhbm6b4gnv">
            Back to Marketplace
          </Link>} data-id="element-10208" data-mp="38e3kq2uuvgd3" />;
  }
  const items = marketItems.filter(item => item.category === category.id);
  return <div className="space-y-8" data-id="element-10210" data-mp="3adzhy8ezor4h">
      <div data-id="element-10211" data-mp="3acod059t5dbs">
        <Link to="/market" className="inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10212" data-mp="3acy2zsuurkhh">
          <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-10213" data-mp="3j7uljc0gq9i0" />
          Marketplace
        </Link>
        <h1 className="mt-2 text-h1 font-semibold tracking-tight text-ink" data-id="element-10214" data-mp="3o5e0yf8wcsbj">{category.label}</h1>
        <p className="mt-2 max-w-[62ch] text-body-lg text-ink-muted" data-id="element-10215" data-mp="2rhpvawq0us8v">{category.blurb}</p>
        <p className="mt-3 text-body-sm text-ink-muted" data-id="element-10216" data-mp="2vi0cl5j9l531">
          Showing what is offered around {campusName}.
        </p>
      </div>

      {/* Move between categories without going back to the marketplace home. */}
      <nav aria-label="Marketplace categories" className="border-y border-line py-2.5" data-id="element-10217" data-mp="35gz8romuf895">
        <div className="cz-tabstrip flex min-w-0 gap-2" data-id="element-10218" data-mp="fpufi2egwe71">
          {marketCategories.map(entry => <Link key={entry.id} to={entry.to} aria-current={entry.id === category.id ? 'page' : undefined} className={`inline-flex min-h-touch shrink-0 items-center rounded-md px-3.5 text-body-sm font-medium ${entry.id === category.id ? 'bg-brand-50 text-brand-800' : 'text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-10219" data-mp="knm4oonq9m7j">
              {entry.label}
            </Link>)}
        </div>
      </nav>

      {/* A category whose real product lives elsewhere says so, and hands over. */}
      {category.handedTo && <div className="rounded-card border border-brand-200 bg-brand-50 px-5 py-4" data-id="element-10220" data-mp="1ci0t3i37ruf3">
          <p className="flex items-start gap-2.5 text-body-sm text-ink" data-id="element-10221" data-mp="17k8zmjmx808s">
            <InfoIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" data-id="element-10222" data-mp="17m3na5pku6gk" />
            <span data-id="element-10223" data-mp="36uqvypbk8dym">
              <strong className="font-semibold" data-id="element-10224" data-mp="31xqp4c6vz76g">
                This is discovery only. {category.handedTo.domain} is where it actually happens.
              </strong>{' '}
              {category.handedTo.why}
            </span>
          </p>
          <Link to={category.handedTo.to} className="mt-3 inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10225" data-mp="3ew80tenxtj60">
            Open {category.handedTo.domain}
            <ArrowRightIcon aria-hidden className="h-4 w-4" data-id="element-10226" data-mp="35169cc66w509" />
          </Link>
        </div>}

      {category.pattern === 'ordering' && <OrderingView items={items} data-id="element-10227" data-mp="2vyx15sye8nq1" />}
      {category.pattern === 'goods' && <GoodsView items={items} data-id="element-10228" data-mp="2h9slojizgxlm" />}
      {category.pattern === 'property' && <PropertyView items={items} data-id="element-10229" data-mp="2hblqrudy79ri" />}
      {category.pattern === 'booking' && <BookingView items={items} data-id="element-10230" data-mp="2m9na8gfadb3i" />}
      {category.pattern === 'gig' && <GigView items={items} data-id="element-10231" data-mp="289cf316y1acj" />}
      {category.pattern === 'discovery' && <DiscoveryView items={items} data-id="element-10232" data-mp="28b71kjxv5aiq" />}
    </div>;
}

/* ------------------------------------------------------------------ ordering */

function OrderingView({
  items
}: {
  items: MarketItem[];
}) {
  const tags = Array.from(new Set(items.flatMap(item => item.tags ?? [])));
  const [tag, setTag] = useState<string>('All');
  const [fulfilment, setFulfilment] = useState<'Delivery' | 'Pickup'>('Delivery');
  const [lines, setLines] = useState<Record<string, number>>({});
  const shown = tag === 'All' ? items : items.filter(item => item.tags?.includes(tag));
  const cart = Object.entries(lines).map(([itemId, quantity]) => ({
    item: items.find(entry => entry.id === itemId)!,
    quantity
  })).filter(line => line.item);
  const subtotal = cart.reduce((total, line) => total + (line.item.priceNaira ?? 0) * line.quantity, 0);
  const deliveryFee = fulfilment === 'Delivery' && subtotal > 0 ? 600 : 0;
  const add = (item: MarketItem) => setLines(current => ({
    ...current,
    [item.id]: (current[item.id] ?? 0) + 1
  }));
  const remove = (itemId: string) => setLines(current => {
    const next = {
      ...current
    };
    if ((next[itemId] ?? 0) <= 1) delete next[itemId];else next[itemId] -= 1;
    return next;
  });
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]" data-id="element-10233" data-mp="1s13f5hqaoet8">
      <div className="space-y-5" data-id="element-10234" data-mp="1wyvf8z13f78i">
        <div className="cz-tabstrip flex min-w-0 gap-2" data-id="element-10235" data-mp="1wxtvm660hcf6">
          {['All', ...tags].map(option => <button key={option} type="button" onClick={() => setTag(option)} aria-pressed={tag === option} className={`min-h-touch shrink-0 rounded-md border px-4 text-body-sm font-medium ${tag === option ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-10236" data-mp="21shlo9gnyevv">
              {option}
            </button>)}
        </div>

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-10237" data-mp="qb811jlstgsz">
          {shown.map(item => <ItemCard key={item.id} item={item} pattern="ordering" onAction={add} data-id="element-10238" data-mp="irytzx0rlu73" />)}
        </div>
      </div>

      <div className="space-y-5" data-id="element-10239" data-mp="e0qmj6izwiwk">
        <Panel title="Your order" bodyClassName="p-0" data-id="element-10240" data-mp="dznv8xhrgeg4">
          <div className="border-b border-line px-5 py-4" data-id="element-10241" data-mp="91mb098lm932">
            <div className="flex gap-2" data-id="element-10242" data-mp="902v4jquu01t">
              {(['Delivery', 'Pickup'] as const).map(option => <button key={option} type="button" onClick={() => setFulfilment(option)} aria-pressed={fulfilment === option} className={`min-h-touch flex-1 rounded-md border px-3 text-body-sm font-medium ${fulfilment === option ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted'}`} data-id="element-10243" data-mp="4182yr6zyz51">
                  {option}
                </button>)}
            </div>
            <p className="mt-2.5 flex items-center gap-1.5 text-caption text-ink-muted" data-id="element-10244" data-mp="38jdjvpooxtnv">
              <ClockIcon aria-hidden className="h-4 w-4" data-id="element-10245" data-mp="1bfbs7s2vpco5" />
              {fulfilment === 'Delivery' ? 'Delivery windows are set by each vendor, not by Campuzon.' : 'Collect from the vendor. Check their opening hours before you set out.'}
            </p>
          </div>

          {cart.length === 0 ? <p className="px-5 py-6 text-body-sm text-ink-muted" data-id="element-10246" data-mp="1v4vx4rfui0qw">
              Nothing added yet. Your order stays on this device until you place it.
            </p> : <>
              <ul className="divide-y divide-line" data-id="element-10247" data-mp="1lf2ljqfpdlmn">
                {cart.map(line => <li key={line.item.id} className="px-5 py-3.5" data-id="element-10248" data-mp="1qdxehl7dwqkh">
                    <div className="flex items-start justify-between gap-3" data-id="element-10249" data-mp="1qc2uc93xt2hg">
                      <div className="min-w-0" data-id="element-10250" data-mp="2e2q30wlofw52">
                        <p className="text-body-sm font-medium text-ink" data-id="element-10251" data-mp="2iu82thbyrqog">{line.item.title}</p>
                        <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-10252" data-mp="2iw19gwmkuawg">
                          {naira(line.item.priceNaira ?? 0)} each
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5" data-id="element-10253" data-mp="31ytq2kun066f">
                        <button type="button" onClick={() => remove(line.item.id)} aria-label={`Remove one ${line.item.title}`} className="flex h-11 w-11 items-center justify-center rounded-md border border-line text-ink-muted hover:text-ink" data-id="element-10254" data-mp="31y1xliwc6okd">
                          {line.quantity === 1 ? <Trash2Icon aria-hidden className="h-4 w-4" data-id="element-10255" data-mp="2e98vf4p4fkhb" /> : <MinusIcon aria-hidden className="h-4 w-4" data-id="element-10256" data-mp="3un8t090huy1e" />}
                        </button>
                        <span className="cz-num w-6 text-center text-body font-semibold text-ink" data-id="element-10257" data-mp="3pncpeavpl8md">
                          {line.quantity}
                        </span>
                        <button type="button" onClick={() => add(line.item)} aria-label={`Add one ${line.item.title}`} className="flex h-11 w-11 items-center justify-center rounded-md border border-line text-ink-muted hover:text-ink" data-id="element-10258" data-mp="27guejwa4skhs">
                          <PlusIcon aria-hidden className="h-4 w-4" data-id="element-10259" data-mp="1tnwevxqno68f" />
                        </button>
                      </div>
                    </div>
                  </li>)}
              </ul>

              <dl className="space-y-2 border-t border-line px-5 py-4" data-id="element-10260" data-mp="1esq6x5801xcg">
                <div className="flex justify-between text-body-sm" data-id="element-10261" data-mp="1eujdylmiyjkz">
                  <dt className="text-ink-muted" data-id="element-10262" data-mp="3e4rn1bhi748t">Items</dt>
                  <dd className="cz-num font-medium text-ink" data-id="element-10263" data-mp="396q1mjwlusu6">{naira(subtotal)}</dd>
                </div>
                <div className="flex justify-between text-body-sm" data-id="element-10264" data-mp="3ho05iklq4z9x">
                  <dt className="text-ink-muted" data-id="element-10265" data-mp="3hlnqaf7cp30k">
                    {fulfilment === 'Delivery' ? 'Delivery' : 'Pickup'}
                  </dt>
                  <dd className="cz-num font-medium text-ink" data-id="element-10266" data-mp="3qfk0cvn8qozm">
                    {deliveryFee === 0 ? 'No charge' : naira(deliveryFee)}
                  </dd>
                </div>
                <div className="flex justify-between border-t border-line pt-2 text-body" data-id="element-10267" data-mp="2yow1xrcybxpn">
                  <dt className="font-semibold text-ink" data-id="element-10268" data-mp="32qhjvr4cf26p">Total</dt>
                  <dd className="cz-num font-semibold text-ink" data-id="element-10269" data-mp="32qrangx7pddg">{naira(subtotal + deliveryFee)}</dd>
                </div>
              </dl>

              <div className="border-t border-line px-5 py-4" data-id="element-10270" data-mp="3codsoc9t5l1n">
                <FeatureGate featureKey="escrow" data-id="element-10271" data-mp="3cpyo043y580k" />
              </div>
            </>}
        </Panel>

        <Panel title="Vendor hours" data-id="element-10272" data-mp="n1fxm47io5ac">
          <ul className="space-y-3" data-id="element-10273" data-mp="rxwrlf1eddvc">
            {Array.from(new Set(items.map(item => item.sellerId))).map(sellerId => {
            const seller = sellerFor(sellerId);
            if (!seller) return null;
            return <li key={sellerId} className="flex items-baseline justify-between gap-3" data-id="element-10274" data-mp="3tgc0ruluctbf">
                  <span className="text-body-sm text-ink" data-id="element-10275" data-mp="c7yhq483kt49">{seller.displayName}</span>
                  <span className="shrink-0 text-caption text-ink-muted" data-id="element-10276" data-mp="7af0qwk0m28m">
                    {seller.hoursLabel ?? 'Hours not published'}
                  </span>
                </li>;
          })}
          </ul>
        </Panel>
      </div>
    </div>;
}

/* --------------------------------------------------------------------- goods */

const CONDITIONS: Condition[] = ['New', 'Like new', 'Good', 'Fair'];
function GoodsView({
  items
}: {
  items: MarketItem[];
}) {
  const [condition, setCondition] = useState<Condition | 'Any'>('Any');
  const [myCoursesOnly, setMyCoursesOnly] = useState(false);
  const registered = useMemo(() => Array.from(new Set(timetable.map(session => session.courseCode))), []);
  const kinds = Array.from(new Set(items.flatMap(item => item.tags ?? [])));
  const [kind, setKind] = useState<string>('All');
  const shown = items.filter(item => {
    if (kind !== 'All' && !item.tags?.includes(kind)) return false;
    if (condition !== 'Any' && item.condition !== condition) return false;
    if (myCoursesOnly && !item.courseCodes?.some(code => registered.includes(code))) return false;
    return true;
  });
  return <div className="space-y-5" data-id="element-10277" data-mp="1v7o99yjjlmjv">
      <div className="cz-tabstrip flex min-w-0 gap-2" data-id="element-10278" data-mp="1v5tl8bcz5ebk">
        {['All', ...kinds].map(option => <button key={option} type="button" onClick={() => setKind(option)} aria-pressed={kind === option} className={`min-h-touch shrink-0 rounded-md border px-4 text-body-sm font-medium ${kind === option ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-10279" data-mp="1h6tyxacxpbik">
            {option}
          </button>)}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-card border border-line bg-surface px-4 py-3.5" data-id="element-10280" data-mp="l8n5sznx5q17">
        <div className="flex min-w-0 flex-wrap items-center gap-2" data-id="element-10281" data-mp="gbekiq7h55vq">
          <span className="text-body-sm font-medium text-ink" data-id="element-10282" data-mp="bd3aofh6ivdi">Condition</span>
          {(['Any', ...CONDITIONS] as const).map(option => <button key={option} type="button" onClick={() => setCondition(option)} aria-pressed={condition === option} className={`min-h-touch rounded-md border px-3.5 text-body-sm font-medium ${condition === option ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-10283" data-mp="18xg5963r2g0k">
              {option}
            </button>)}
        </div>
        <label className="ml-auto flex min-h-touch items-center gap-2.5 text-body-sm text-ink" data-id="element-10284" data-mp="1v8r7w0jtm9go">
          <input type="checkbox" checked={myCoursesOnly} onChange={event => setMyCoursesOnly(event.target.checked)} className="h-5 w-5 rounded border-line-strong text-brand-700" data-id="element-10285" data-mp="1z9b85czoxt71" />
          Only for my registered courses
        </label>
      </div>

      {myCoursesOnly && <p className="text-body-sm text-ink-muted" data-id="element-10286" data-mp="2dy7cayymmm2d">
          Matched against {registered.join(', ')} — the courses you are timetabled for. Campuzon does
          not know which books your lecturer actually requires.
        </p>}

      {shown.length === 0 ? <EmptyState title="Nothing matches those filters" description="Loosen the condition, or clear the course filter. Nothing is being hidden — there simply is not a listing that fits." data-id="element-10287" data-mp="2ryqgtw3vv9zs" /> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10288" data-mp="xkz9x4cxv481">
          {shown.map(item => <ItemCard key={item.id} item={item} pattern="goods" data-id="element-10289" data-mp="20vwkt2eub439" />)}
        </div>}
    </div>;
}

/* ------------------------------------------------------------------ property */

function PropertyView({
  items
}: {
  items: MarketItem[];
}) {
  const [maxDistance, setMaxDistance] = useState(5);
  const [maxRent, setMaxRent] = useState(500000);
  const [roomType, setRoomType] = useState<string>('Any');
  const [required, setRequired] = useState<string[]>([]);
  const roomTypes = Array.from(new Set(items.flatMap(item => item.tags ?? [])));
  const allAmenities = Array.from(new Set(items.flatMap(item => item.amenities ?? [])));
  const toggleAmenity = (amenity: string) => setRequired(current => current.includes(amenity) ? current.filter(entry => entry !== amenity) : [...current, amenity]);
  const shown = items.filter(item => {
    if ((item.distanceKm ?? 0) > maxDistance) return false;
    if ((item.priceNaira ?? 0) > maxRent) return false;
    if (roomType !== 'Any' && !item.tags?.includes(roomType)) return false;
    if (required.some(amenity => !item.amenities?.includes(amenity))) return false;
    return true;
  });
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]" data-id="element-10290" data-mp="378q0srtqdk5l">
      <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start" data-id="element-10291" data-mp="3c7b4j186ylys">
        <Panel title="Narrow it down" data-id="element-10292" data-mp="2yh7m6lcfncem">
          <div className="space-y-6" data-id="element-10293" data-mp="2yg625rdfvfkr">
            <div data-id="element-10294" data-mp="2yf3cfmrurj6f">
              <label htmlFor="rent" className="text-body-sm font-medium text-ink" data-id="element-10295" data-mp="337ebyoiol2yg">
                Up to <span className="cz-num font-semibold" data-id="element-10296" data-mp="2oirscam0tp1f">{naira(maxRent)}</span> a year
              </label>
              <input id="rent" type="range" min={100000} max={500000} step={20000} value={maxRent} onChange={event => setMaxRent(Number(event.target.value))} className="mt-3 w-full accent-brand-700" data-id="element-10297" data-mp="2thl7s8kvbq48" />
            </div>

            <div data-id="element-10298" data-mp="2zx3kzy2081l9">
              <label htmlFor="distance" className="text-body-sm font-medium text-ink" data-id="element-10299" data-mp="2v03ejm18swtm">
                Within <span className="cz-num font-semibold" data-id="element-10300" data-mp="2uy8ue9xsp8ql">{maxDistance} km</span> of campus
              </label>
              <input id="distance" type="range" min={0.5} max={5} step={0.5} value={maxDistance} onChange={event => setMaxDistance(Number(event.target.value))} className="mt-3 w-full accent-brand-700" data-id="element-10301" data-mp="2l1xd15jclspa" />
              <p className="mt-2 text-caption text-ink-muted" data-id="element-10302" data-mp="27bag9ix5ecvu">
                Measured to the main gate in a straight line, not by the route you would walk.
              </p>
            </div>

            <fieldset data-id="element-10303" data-mp="1xb9u7p7w9iny">
              <legend className="text-body-sm font-medium text-ink" data-id="element-10304" data-mp="1xbjhh51k1bpy">Room type</legend>
              <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-10305" data-mp="1tb9em19271fb">
                {['Any', ...roomTypes].map(option => <button key={option} type="button" onClick={() => setRoomType(option)} aria-pressed={roomType === option} className={`min-h-touch rounded-md border px-3.5 text-body-sm font-medium ${roomType === option ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-10306" data-mp="8ursqxcaej8y">
                    {option}
                  </button>)}
              </div>
            </fieldset>

            <fieldset data-id="element-10307" data-mp="3u3zs64hzsc3">
              <legend className="text-body-sm font-medium text-ink" data-id="element-10308" data-mp="3udvmac84tpp">Must have</legend>
              <div className="mt-2.5 space-y-2" data-id="element-10309" data-mp="32ikrk28butrc">
                {allAmenities.map(amenity => <label key={amenity} className="flex min-h-touch items-center gap-2.5 text-body-sm text-ink" data-id="element-10310" data-mp="37hnmgtulfjll">
                    <input type="checkbox" checked={required.includes(amenity)} onChange={() => toggleAmenity(amenity)} className="h-5 w-5 rounded border-line-strong text-brand-700" data-id="element-10311" data-mp="2kf2x6jr8nmtx" />
                    {amenity}
                  </label>)}
              </div>
            </fieldset>
          </div>
        </Panel>

        <Panel title="What inspected means" data-id="element-10312" data-mp="21pmt5mgchc8r">
          <p className="text-body-sm text-ink" data-id="element-10313" data-mp="26ny3dyajxori">
            A place marked inspected has been visited by a Campuzon representative who confirmed it
            exists and matches the listing. An uninspected place can still be listed, and says so.
            Neither is a statement about the landlord.
          </p>
        </Panel>
      </aside>

      <div className="space-y-4" data-id="element-10314" data-mp="1ixbxack3yek5">
        <p className="text-body-sm text-ink-muted" data-id="element-10315" data-mp="1iw9603ivia3p">
          <span className="cz-num font-semibold text-ink" data-id="element-10316" data-mp="3it3a8lh2wk">{shown.length}</span> of {items.length}{' '}
          places match.
        </p>
        {shown.length === 0 ? <EmptyState title="Nothing matches those filters" description="Raise the rent ceiling, widen the distance, or drop a required amenity. Nothing is being withheld — no listing fits." data-id="element-10317" data-mp="3n7yq3u4ahc6f" /> : <div className="grid gap-4 sm:grid-cols-2" data-id="element-10318" data-mp="3dakj0l499zqs">
            {shown.map(item => <ItemCard key={item.id} item={item} pattern="property" data-id="element-10319" data-mp="38hzrjq8tnyqf" />)}
          </div>}
      </div>
    </div>;
}

/* ------------------------------------------------------------------- booking */

/** What each service family actually covers, and where its limit is. */
const SERVICE_GROUPS: Record<string, string> = {
  Academic: 'Tutoring, research assistance, project support, printing and editing. Help with your work — never work submitted in your name.',
  Creative: 'Graphic design, photography, video and writing.',
  Technical: 'CAD, programming, electronics, engineering and repairs.',
  Business: 'Accounting, marketing, business support and consulting.'
};
function BookingView({
  items
}: {
  items: MarketItem[];
}) {
  const groups = Array.from(new Set(items.flatMap(item => item.tags ?? [])));
  const [group, setGroup] = useState<string>('All');
  const shown = group === 'All' ? items : items.filter(item => item.tags?.includes(group));
  return <div className="space-y-6" data-id="element-10320" data-mp="33mex9weg1s1o">
      <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-10321" data-mp="33ldknnhs4xhu">
        <p className="text-body-sm text-ink" data-id="element-10322" data-mp="2ptp6zfrvu0yr">
          <strong className="font-semibold" data-id="element-10323" data-mp="2psxmp13qjpns">How paying for a service works.</strong> You agree a
          price, the money is held rather than passed straight to the provider, and it is released
          when the work is delivered and you accept it. A dispute pauses the release for review.
        </p>
        <div className="mt-3" data-id="element-10324" data-mp="1axo095u2xzmc">
          <FeatureGate featureKey="escrow" inline data-id="element-10325" data-mp="1aww2bod0fpsw" />
        </div>
      </div>

      <div data-id="element-10326" data-mp="15yukt7v8fyjj">
        <div className="cz-tabstrip flex min-w-0 gap-2" data-id="element-10327" data-mp="15z4dx4bkqltg">
          {['All', ...groups].map(option => <button key={option} type="button" onClick={() => setGroup(option)} aria-pressed={group === option} className={`min-h-touch shrink-0 rounded-md border px-4 text-body-sm font-medium ${group === option ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-10328" data-mp="1knqsv4xahbk5">
              {option}
            </button>)}
        </div>
        {SERVICE_GROUPS[group] && <p className="mt-3 max-w-[66ch] text-body-sm text-ink-muted" data-id="element-10329" data-mp="2dc4oje0wnvsj">{SERVICE_GROUPS[group]}</p>}
      </div>

      {shown.length === 0 ? <EmptyState title="Nothing offered here yet" description="No provider around your campus currently offers this. It is not hidden — nobody has listed it." data-id="element-10330" data-mp="28c9zzh4lrian" /> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10331" data-mp="mvknv4dmdftm">
          {shown.map(item => <ItemCard key={item.id} item={item} pattern="booking" data-id="element-10332" data-mp="qv2y967te8i7" />)}
        </div>}
    </div>;
}

/* ----------------------------------------------------------------------- gig */

function GigView({
  items
}: {
  items: MarketItem[];
}) {
  const [side, setSide] = useState<'hire' | 'offer'>('hire');
  return <div className="space-y-6" data-id="element-10333" data-mp="15rk6hokpj70j">
      <div className="rounded-panel border border-line bg-surface px-5 py-5" data-id="element-10334" data-mp="1ak3mstas4g91">
        <h2 className="text-h4 font-semibold text-ink" data-id="element-10335" data-mp="1em7787ffev6g">Earn, hire, get things done</h2>
        <p className="mt-2 max-w-[62ch] text-body text-ink-muted" data-id="element-10336" data-mp="1eo1isv4t5axv">
          Gigs are student to student. The same account can be on either side of one, and being paid
          for a gig does not make you a registered business.
        </p>
        <div className="mt-4 flex gap-2" data-id="element-10337" data-mp="1yol9rjv4a10r">
          {[{
          id: 'hire' as const,
          label: 'Find someone to help'
        }, {
          id: 'offer' as const,
          label: 'Offer a service'
        }].map(option => <button key={option.id} type="button" onClick={() => setSide(option.id)} aria-pressed={side === option.id} className={`min-h-touch-lg flex-1 rounded-md border px-4 text-body font-medium ${side === option.id ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-10338" data-mp="1oqnon58k0kcd">
              {option.label}
            </button>)}
        </div>
      </div>

      {side === 'hire' ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10339" data-mp="306ca1s65tbb2">
          {items.map(item => <ItemCard key={item.id} item={item} pattern="gig" data-id="element-10340" data-mp="2vdj4j22v9yxx" />)}
        </div> : <Panel title="Offer a gig" description="What you list, what you charge and how long it takes are yours to set." data-id="element-10341" data-mp="tid0z84jaga4">
          <ul className="space-y-3 text-body-sm text-ink" data-id="element-10342" data-mp="jr8nydyf55hx">
            {['Describe one specific piece of work, not a general skill.', 'Set a price and say what it includes, so nobody negotiates after the fact.', 'Say how long you need. Missing a stated turnaround affects your reputation.', 'Academic work must be help, not substitution. Writing something for submission is refused.'].map(rule => <li key={rule} className="flex items-start gap-2.5" data-id="element-10343" data-mp="u7iks0yd3dg">
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-700" data-id="element-10344" data-mp="5sist3v5tfw7" />
                {rule}
              </li>)}
          </ul>
          <div className="mt-4" data-id="element-10345" data-mp="2058n3mpfwr44">
            <Button variant="primary" data-id="element-10346" data-mp="20479bah1hqip">Create a gig listing</Button>
          </div>
        </Panel>}
    </div>;
}

/* ----------------------------------------------------------------- discovery */

function DiscoveryView({
  items
}: {
  items: MarketItem[];
}) {
  if (items.length === 0) {
    return <EmptyState title="Nothing listed here" description="This category exists so you can find your way to the part of Campuzon that owns it. There is no separate inventory to browse." data-id="element-10347" data-mp="3uex83tqczt24" />;
  }
  return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10348" data-mp="7ahd1fe85tuw">
      {items.map(item => <ItemCard key={item.id} item={item} pattern="discovery" data-id="element-10349" data-mp="3949xd2io4ro" />)}
    </div>;
}